import React, { useEffect } from 'react';
import { DollarSign, Clock, Car, Shield, MapPin, Eye, UserCheck, Calculator } from 'lucide-react';
import { parseDecimal } from '../utils/utils';
import NumberInput from './NumberInput';

interface Props {
    formValues: any;
    setFormValues: (data: any) => void;
}

// Aliquota usata solo per l'anteprima del totale: il calcolo vero è lato server.
const IVA_DEFAULT_PERCENT = 22;

/** Live preview totale ivato a partire da un imponibile inserito dall'utente. */
const formatTotaleIvato = (imponibile: string | number | undefined, aliquotaPercent: number): string => {
    // Accetta sia "0,5" che "0.5", rifiuta i negativi.
    const n = parseDecimal(imponibile);
    if (n <= 0) return '';
    const totale = n * (1 + aliquotaPercent / 100);
    // Format italiano con due decimali; il backend resta autoritativo.
    return totale.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const Step_Economico: React.FC<Props> = ({ formValues, setFormValues }) => {

    const handleChange = (key: string, value: any) => {
        setFormValues((prev: any) => ({ ...prev, [key]: value }));
    };

    // Al cambio di tipologia si azzerano i campi della modalità precedente:
    //  • a corpo: via le tariffe orarie (gli accordi specifici valgono per entrambe);
    //  • a consuntivo: via l'importo preventivato, che qui è solo un budget opzionale.
    const handleChangeTipoCompenso = (tipo: 'consuntivo' | 'corpo') => {
        setFormValues((prev: any) => {
            const next: any = { ...prev, tipo_compenso: tipo };
            if (tipo === 'corpo') {
                next.tariffa_oraria_feriale = '';
                next.tariffa_oraria_festiva = '';
                next.tariffa_giornaliera = '';
            } else {
                next.importo_preventivato = '';
            }
            return next;
        });
    };

    const isCorpo = formValues.tipo_compenso === 'corpo';

    // A corpo le tariffe orarie devono essere vuote anche se arrivano valorizzate
    // da una bozza o da fuori. Gli accordi specifici restano. Non aggiorna lo
    // stato se sono già vuote, quindi niente loop.
    useEffect(() => {
        if (formValues.tipo_compenso !== 'corpo') return;
        const dirty =
            !!formValues.tariffa_oraria_feriale ||
            !!formValues.tariffa_oraria_festiva ||
            !!formValues.tariffa_giornaliera;
        if (!dirty) return;
        setFormValues((prev: any) => ({
            ...prev,
            tariffa_oraria_feriale: '',
            tariffa_oraria_festiva: '',
            tariffa_giornaliera: '',
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formValues.tipo_compenso,
        formValues.tariffa_oraria_feriale,
        formValues.tariffa_oraria_festiva,
        formValues.tariffa_giornaliera,
    ]);

    return (
        <div className="animate-in fade-in slide-in-from-right-8 max-w-4xl mx-auto space-y-6 pb-24">

            {/* 1. Condizioni economiche e tariffe */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 border-t-4 border-t-green-500">
                <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2 border-b border-gray-100 pb-4">
                    <DollarSign size={24} className="text-green-600"/> Condizioni Economiche e Tariffe
                </h3>

                {/* Scelta Tipologia */}
                <div className="mb-6">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">1. Tipologia di Fatturazione</label>
                    <div className="grid grid-cols-2 gap-4">
                        {(['consuntivo', 'corpo'] as const).map(tipo => (
                            <label key={tipo} className={`p-4 border rounded-xl cursor-pointer transition-all ${formValues.tipo_compenso === tipo ? 'border-green-500 bg-green-50 ring-2 ring-green-200 shadow-sm' : 'hover:bg-gray-50 border-gray-200'}`}>
                                <div className="flex items-center gap-3">
                                    <input type="radio" name="tipo_compenso" className="w-4 h-4 text-green-600 focus:ring-green-500"
                                           checked={formValues.tipo_compenso === tipo} onChange={() => handleChangeTipoCompenso(tipo)} />
                                    <div>
                                        <div className="font-bold text-sm uppercase text-gray-900">{tipo === 'corpo' ? 'A CORPO (Forfait)' : 'A CONSUNTIVO (A Ore)'}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {tipo === 'corpo' ? 'Importo fisso pattuito, indipendente dalle ore reali' : 'Fatturazione basata sulle ore e spese effettive'}
                                        </div>
                                    </div>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Tariffe Orarie / Giornaliere + Accordi Specifici */}
                <div className="mb-6 bg-gray-50/50 p-5 rounded-xl border border-gray-100">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <Calculator size={14} className="text-blue-600"/> 2. TARIFFE APPLICATE
                        <span className="text-gray-500 normal-case tracking-normal font-normal ml-1">(solo a consuntivo)</span>
                    </label>

                    {/* Griglia a 4 colonne (solo tariffe orarie) */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isCorpo ? 'text-gray-400' : 'text-gray-600'}`}>Oraria Feriale (€/h + IVA)</label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none shadow-sm disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                   placeholder="Es. 50"
                                   disabled={isCorpo}
                                   min={0}
                                   value={formValues.tariffa_oraria_feriale || ''} onChange={e => handleChange('tariffa_oraria_feriale', e.target.value)} />
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isCorpo ? 'text-gray-400' : 'text-gray-600'}`}>Festiva/Notturna (€/h + IVA)</label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none shadow-sm disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                   placeholder="Es. 70"
                                   disabled={isCorpo}
                                   min={0}
                                   value={formValues.tariffa_oraria_festiva || ''} onChange={e => handleChange('tariffa_oraria_festiva', e.target.value)} />
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isCorpo ? 'text-gray-400' : 'text-gray-600'}`}>Giornaliera (€/g + IVA)</label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none shadow-sm disabled:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed"
                                   placeholder="Es. 400"
                                   disabled={isCorpo}
                                   min={0}
                                   value={formValues.tariffa_giornaliera || ''} onChange={e => handleChange('tariffa_giornaliera', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Car size={12}/> Rimborso Km (€/km + IVA)</label>
                            <NumberInput step="0.01" className="w-full p-2.5 border rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-colors"
                                         placeholder="Es. 0.50"
                                         min={0}
                                         value={formValues.rimborso_km || ''} onChange={e => handleChange('rimborso_km', e.target.value)} />
                        </div>
                    </div>

                    {/* Accordi specifici: validi sia a consuntivo sia a corpo. */}
                    <div className="border-t border-gray-200 pt-4">
                        <label className="block text-xs font-medium mb-1 text-gray-600">Accordi Specifici</label>
                        <input type="text" className="w-full p-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400 outline-none shadow-sm"
                               placeholder="Es. servizio dalle ore 08:00 alle 23:00 - 3 operatori - scontato a 800"
                               value={formValues.accordi_specifici || ''} onChange={e => handleChange('accordi_specifici', e.target.value)} />
                    </div>
                </div>

                {/* Stima Ore e Importi Totali */}
                <div className="mb-6">
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">3. Stima e Importi Totali</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="bg-amber-50/50 p-4 rounded-xl border border-amber-100">
                            <label className="block text-xs font-bold text-amber-800 mb-1 flex items-center gap-1"><Clock size={14}/> N° Ore Stimate / Minime</label>
                            <NumberInput className="w-full p-2.5 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-400 outline-none"
                                   placeholder="Es. 20"
                                   min={0}
                                   value={formValues.num_ore || ''} onChange={e => handleChange('num_ore', e.target.value)} />
                            <p className="text-[10px] text-amber-600 mt-1 leading-tight">Monte ore su cui si basa il preventivo o ore minime garantite.</p>
                        </div>
                        <div className="bg-green-50/50 p-4 rounded-xl border border-green-200 shadow-sm">
                            <label className="block text-xs font-bold text-green-800 mb-1 flex items-center gap-1">
                                <DollarSign size={14}/>
                                {formValues.tipo_compenso === 'corpo' ? 'Importo Forfait Pattuito (€ + IVA)' : 'Budget Massimo Stimato (€ + IVA)'}
                            </label>
                            <NumberInput className="w-full p-2.5 border border-green-300 rounded-lg font-bold text-green-900 focus:ring-2 focus:ring-green-500 outline-none"
                                   placeholder="0.00"
                                   min={0}
                                   value={formValues.importo_preventivato || ''} onChange={e => handleChange('importo_preventivato', e.target.value)} />
                            {/* Anteprima del totale IVA inclusa. */}
                            {formatTotaleIvato(formValues.importo_preventivato, IVA_DEFAULT_PERCENT) && (
                                <p className="text-[11px] font-semibold text-green-700 mt-1.5">
                                    Totale con IVA {IVA_DEFAULT_PERCENT}%: € {formatTotaleIvato(formValues.importo_preventivato, IVA_DEFAULT_PERCENT)}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Anticipi, Cauzioni e Rimborsi Specifici */}
                <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">4. Anticipi, Cauzioni e Rimborsi</label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Acconto Richiesto (€ + IVA)</label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-400 outline-none transition-colors"
                                   placeholder="0.00"
                                   min={0}
                                   value={formValues.acconto_compenso || formValues.acconto || ''} onChange={e => handleChange('acconto_compenso', e.target.value)} />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Deposito Cauzionale (€ + IVA)</label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-green-400 outline-none transition-colors"
                                   placeholder="0.00"
                                   min={0}
                                   value={formValues.cauzione || ''} onChange={e => handleChange('cauzione', e.target.value)} />
                        </div>
                        {/* Massimale rimborso per perdita/danneggiamento apparecchiature
                            (pag. 5 del mandato), in euro interi, scelto per ogni mandato. */}
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                                <Shield size={12}/> Massimale perdita apparati GPS (€ + IVA)
                            </label>
                            <NumberInput className="w-full p-2.5 border rounded-lg text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-colors"
                                   placeholder="Es. 1500"
                                   min={0}
                                   value={formValues.apparati_localizzazione || ''} onChange={e => handleChange('apparati_localizzazione', e.target.value)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Autorizzazioni speciali */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                    <Shield className="text-purple-600"/> Autorizzazioni Speciali
                </h3>
                <p className="text-xs text-gray-500 mb-5">Le seguenti autorizzazioni vengono riportate nel corpo del contratto.</p>

                <div className="space-y-4">
                    {/* Localizzazione satellitare GPS */}
                    <AuthorizationRow
                        id="localizzazione_satellitare"
                        icon={<MapPin size={14}/>}
                        label="Autorizzazione Apparati di Localizzazione Satellitare (GPS)"
                        description="Il cliente autorizza l'installazione di dispositivi GPS sul veicolo del soggetto investigato"
                        checked={!!formValues.localizzazione_satellitare}
                        onChange={v => handleChange('localizzazione_satellitare', v)}
                    />

                    {/* Riprese audio/video */}
                    <AuthorizationRow
                        id="ripresa_av"
                        icon={<Eye size={14}/>}
                        label="Autorizzazione a utilizzo di apparati di ripresa e registrazione"
                        description="Il cliente autorizza la registrazione audio/video per documentare le attività investigative"
                        checked={!!formValues.ripresa}
                        onChange={v => handleChange('ripresa', v)}
                    />

                    {/* Altro: testo libero, stampato a pag. 6 del mandato (sezione 4
                        "Si concorda espressamente l'utilizzo nelle attività di:"). */}
                    <div className={`p-4 rounded-xl border transition-all ${formValues.testo_altro_apparati ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200 bg-gray-50/50'}`}>
                        <div className="text-sm font-bold text-gray-800 mb-1.5">Altri apparati / strumenti autorizzati</div>
                        <div className="text-xs text-gray-500 mb-2">Testo libero (es. droni, contromisure, microfoni direzionali). Lasciare vuoto se non applicabile.</div>
                        <input type="text" className="w-full p-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-400 outline-none shadow-sm"
                               placeholder="Es. drone DJI Mini 3, microfono direzionale..."
                               value={formValues.testo_altro_apparati || ''} onChange={e => handleChange('testo_altro_apparati', e.target.value)} />
                    </div>

                    {/* Investigatori esterni delegati: stampati a pag. 12 del mandato,
                        uno per riga, al massimo 5. */}
                    <div className={`p-4 rounded-xl border transition-all ${formValues.investigatori_esterni ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200 bg-gray-50/50'}`}>
                        <div className="text-sm font-bold text-gray-800 mb-1.5">Investigatori esterni eventualmente delegati</div>
                        <div className="text-xs text-gray-500 mb-2">Opzionale. Inserisci un nominativo per riga (max 5 visibili in PDF). Lasciare vuoto se nessuno è designato in fase di firma.</div>
                        <textarea rows={3} className="w-full p-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-400 outline-none shadow-sm resize-y"
                                  placeholder={'Mario Rossi - Studio Investigativo X\nLucia Bianchi - Agenzia Y'}
                                  value={formValues.investigatori_esterni || ''} onChange={e => handleChange('investigatori_esterni', e.target.value)} />
                    </div>
                </div>
            </div>

            {/* GDPR - nomina responsabile */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2 border-b pb-2">
                    <UserCheck className="text-teal-600"/> GDPR – Nomina Responsabile del Trattamento (MANDATI CONFERITI DA AZIENDE, ENTI, PROFESSIONISTI)
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className={`p-4 border rounded-xl cursor-pointer transition-all ${formValues.nomina_responsabile === 'si' ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-400' : 'hover:bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start gap-3">
                            <input type="radio" name="nomina_responsabile" className="mt-1 w-4 h-4 text-teal-600 focus:ring-teal-500"
                                   checked={formValues.nomina_responsabile === 'si'} onChange={() => handleChange('nomina_responsabile', 'si')} />
                            <div>
                                <div className="font-bold text-sm text-gray-900 uppercase">SÌ</div>
                                <div className="text-xs text-gray-600 mt-1">
                                    Il Mandante nomina l'Agenzia come Responsabile esterno del Trattamento (art. 28 GDPR).
                                </div>
                            </div>
                        </div>
                    </label>

                    <label className={`p-4 border rounded-xl cursor-pointer transition-all ${formValues.nomina_responsabile === 'no' ? 'border-gray-500 bg-gray-100 ring-1 ring-gray-400' : 'hover:bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start gap-3">
                            <input type="radio" name="nomina_responsabile" className="mt-1 w-4 h-4 text-gray-600 focus:ring-gray-500"
                                   checked={formValues.nomina_responsabile === 'no'} onChange={() => handleChange('nomina_responsabile', 'no')} />
                            <div>
                                <div className="font-bold text-sm text-gray-900 uppercase">NO</div>
                                <div className="text-xs text-gray-600 mt-1">
                                    Il Mandante NON nomina l'Agenzia. Non verrà inclusa la clausola di nomina.
                                </div>
                            </div>
                        </div>
                    </label>
                </div>
            </div>

        </div>
    );
};

/* Riga autorizzazione con toggle */
const AuthorizationRow: React.FC<{
    id: string;
    icon: React.ReactNode;
    label: string;
    description: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    children?: React.ReactNode;
}> = ({ icon, label, description, checked, onChange, children }) => (
    <div className={`p-4 rounded-xl border transition-all ${checked ? 'border-purple-300 bg-purple-50/50' : 'border-gray-200 bg-gray-50/50'}`}>
        <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="mt-0.5 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                   checked={checked} onChange={e => onChange(e.target.checked)} />
            <div className="flex-1">
                <div className="text-sm font-bold text-gray-800 flex items-center gap-1.5">{icon} {label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{description}</div>
            </div>
        </label>
        {children}
    </div>
);